import { z } from "zod";
import { cuitSchema, yearMonthSchema } from "./dto.js";
import { ROUNDING_TOLERANCE_CENTS, centsToDecimal, decimalToCents, formatCentsAr, } from "./money.js";
import { taxFromNet } from "./net-split.js";
/**
 * Consignment settlements (plan 016), as the previous system's screen «Liquidaciones Mercado:
 * Emitidas» loads them and as the paper ones read (Di Costanzo, Gajo's, EM Frutícola, Sept. 2026):
 * the consignatario sells the comitente's goods (venta bruta, IVA 10,5%) and charges a commission
 * (IVA 10,5%) and unloading (descarga, IVA 21%), retaining Ganancias, IIBB and IVA.
 *
 * It books as two vouchers of the settlement's type and number (Martín, 2026-10-19):
 *   compra to the comitente — venta bruta gravada al 10,5% + IVA crédito, and the no gravado;
 *   venta to the comitente  — comisiones + descargas per rate, with their IVA débito.
 * Retenciones stay on the settlement: practised by the client, they do not touch its Caja IVA.
 */
/**
 * The previous system's «Tipo»: A, B or M (Martín, 2026-10-19). Stored as the ARCA cuenta de
 * venta y líquido producto of that letter — 060, 061, 058 — the same codes the import gives the
 * previous system's «L», so a settlement typed here and one imported share one numbering.
 */
export const SETTLEMENT_TYPES = [
    { code: 60, letter: "A" },
    { code: 61, letter: "B" },
    { code: 58, letter: "M" },
];
/** Every code a stored settlement may have: the three above plus 063/064/059 (liquidación). */
const LETTER_OF = { 58: "M", 59: "M", 60: "A", 61: "B", 63: "A", 64: "B" };
export function settlementLetter(typeCode) {
    return LETTER_OF[typeCode] ?? String(typeCode);
}
/** B settlements discriminate no IVA on the venta bruta. */
export function isSettlementB(typeCode) {
    return typeCode === 61 || typeCode === 64;
}
const amount = z
    .union([z.string(), z.number()])
    .transform((v) => String(v).trim())
    .refine((v) => /^\d+(\.\d{1,2})?$/.test(v), {
    message: "El importe tiene que ser un número positivo con hasta dos decimales (ej. 1234.56).",
})
    .default("0");
export const SETTLEMENT_AMOUNT_FIELDS = [
    "grossSale",
    "nonTaxed",
    "commission105",
    "commission21",
    "unloading105",
    "unloading21",
    "ivaCredit105",
    "ivaDebit21",
    "ivaDebit105",
    "retGanancias",
    "retIibb",
    "retIva",
];
export const settlementInputSchema = z.object({
    activity: z.string().trim().min(1).max(60).default("Comercio"),
    issueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "La fecha tiene que ser AAAA-MM-DD."),
    /** Where its compra and venta are booked: the issue month or a later one; absent, the issue month. */
    period: yearMonthSchema.optional(),
    // The screen offers A/B/M (060/061/058); 063/064/059 stay accepted so a settlement stored
    // with one of them can still be edited (Codex 016).
    typeCode: z.union([z.literal(60), z.literal(61), z.literal(58), z.literal(63), z.literal(64), z.literal(59)], { message: "Elegí el tipo de liquidación: A, B o M." }),
    pointOfSale: z.number().int().min(1).max(99999),
    number: z.number().int().min(1).max(99_999_999),
    counterpart: z.object({
        docType: z.union([z.literal(80), z.literal(86)]),
        docNumber: cuitSchema,
        name: z.string().trim().min(1, "Falta el nombre del comitente.").max(200),
    }),
    grossSale: amount,
    nonTaxed: amount,
    commission105: amount,
    commission21: amount,
    unloading105: amount,
    unloading21: amount,
    ivaCredit105: amount,
    ivaDebit21: amount,
    ivaDebit105: amount,
    retGanancias: amount,
    retIibb: amount,
    retIva: amount,
    total: amount,
});
function cents(input) {
    return Object.fromEntries(SETTLEMENT_AMOUNT_FIELDS.map((key) => [key, decimalToCents(input[key])]));
}
/** Líquido producto: what the comitente gets. Signed: deductions can exceed the sale. */
export function settlementTotalCents(c) {
    return (c.grossSale +
        c.nonTaxed +
        c.ivaCredit105 -
        c.commission105 -
        c.commission21 -
        c.unloading105 -
        c.unloading21 -
        c.ivaDebit21 -
        c.ivaDebit105 -
        c.retGanancias -
        c.retIibb -
        c.retIva);
}
/** The IVA each base calls for, for the screen's automatic fill (editable, as on paper). */
export function settlementSuggestedIva(c) {
    return {
        ivaCredit105: taxFromNet(c.grossSale, "10.5"),
        ivaDebit105: taxFromNet(c.commission105 + c.unloading105, "10.5"),
        ivaDebit21: taxFromNet(c.commission21 + c.unloading21, "21"),
    };
}
const pesos = (c) => `$${formatCentsAr(c)}`;
/** Splits a settlement into its compra and venta, with every check that does not need the database. */
export function bookSettlement(input) {
    const c = cents(input);
    const errors = [];
    const warnings = [];
    const letterB = isSettlementB(input.typeCode);
    if (c.grossSale === 0)
        errors.push("Falta la venta bruta (subtotal) de la liquidación.");
    // The period is chosen, as in the previous system's screen (Martín, 2026-10-19); its compra and
    // its venta both go there, never before the month of its date.
    if (input.period &&
        input.period.year * 12 + input.period.month <
            Number(input.issueDate.slice(0, 4)) * 12 + Number(input.issueDate.slice(5, 7))) {
        errors.push("El período de la liquidación no puede ser anterior al mes de su fecha.");
    }
    if (letterB && c.ivaCredit105 !== 0) {
        errors.push("Una liquidación B no discrimina IVA sobre la venta bruta: dejá el IVA crédito en cero.");
    }
    const computed = settlementTotalCents(c);
    const stated = decimalToCents(input.total);
    if (Math.abs(computed - stated) > ROUNDING_TOLERANCE_CENTS) {
        errors.push(`El total (${pesos(stated)}) no coincide con la venta bruta más IVA menos comisiones, ` +
            `descargas, su IVA y retenciones (${pesos(computed)}).`);
    }
    const suggested = settlementSuggestedIva(c);
    const check = (label, actual, expected) => {
        if (Math.abs(actual - expected) > 1) {
            warnings.push(`${label}: el cálculo da ${pesos(expected)} y la liquidación dice ${pesos(actual)}.`);
        }
    };
    if (!letterB)
        check("IVA 10,5% sobre la venta bruta", c.ivaCredit105, suggested.ivaCredit105);
    check("IVA 10,5% sobre comisiones y descargas", c.ivaDebit105, suggested.ivaDebit105);
    check("IVA 21% sobre comisiones y descargas", c.ivaDebit21, suggested.ivaDebit21);
    const base = {
        typeCode: input.typeCode,
        issueDate: input.issueDate,
        pointOfSale: input.pointOfSale,
        number: input.number,
        counterpart: input.counterpart,
        currency: "ARS",
        exchangeRate: "1",
        ...(input.period ? { period: input.period } : {}),
    };
    const purchaseLines = [];
    if (letterB) {
        purchaseLines.push({
            kind: "no_gravado",
            net: centsToDecimal(c.grossSale + c.nonTaxed),
            tax: "0.00",
        });
    }
    else {
        purchaseLines.push({
            kind: "gravado",
            rate: "10.5",
            net: centsToDecimal(c.grossSale),
            tax: centsToDecimal(c.ivaCredit105),
        });
        if (c.nonTaxed) {
            purchaseLines.push({ kind: "no_gravado", net: centsToDecimal(c.nonTaxed), tax: "0.00" });
        }
    }
    const purchase = {
        ...base,
        direction: "compra",
        total: centsToDecimal(c.grossSale + c.nonTaxed + c.ivaCredit105),
        lines: purchaseLines,
    };
    const net105 = c.commission105 + c.unloading105;
    const net21 = c.commission21 + c.unloading21;
    const saleLines = [];
    if (net105 || c.ivaDebit105) {
        saleLines.push({
            kind: "gravado",
            rate: "10.5",
            net: centsToDecimal(net105),
            tax: centsToDecimal(c.ivaDebit105),
        });
    }
    if (net21 || c.ivaDebit21) {
        saleLines.push({
            kind: "gravado",
            rate: "21",
            net: centsToDecimal(net21),
            tax: centsToDecimal(c.ivaDebit21),
        });
    }
    if ((c.ivaDebit105 && !net105) || (c.ivaDebit21 && !net21)) {
        errors.push("Hay IVA débito sin comisión ni descarga que lo genere.");
    }
    const sale = saleLines.length
        ? {
            ...base,
            direction: "venta",
            total: centsToDecimal(net105 + net21 + c.ivaDebit105 + c.ivaDebit21),
            lines: saleLines,
        }
        : null;
    return { errors, warnings, purchase, sale };
}
//# sourceMappingURL=settlement.js.map